/**
 * The API client.
 *
 * One place that knows how to talk to the server, so error handling, session
 * expiry and the portal header are uniform rather than re-implemented per
 * screen.
 *
 * Authentication is an HttpOnly cookie the browser sends automatically. There
 * is deliberately no token in JavaScript: the previous build kept the signed-in
 * user in localStorage, which any script on the page could read and any XSS
 * could exfiltrate.
 */

import type { Action, Edition, Feature, Module, PhotoTransform, Role } from '@cornerstone/education-domain'

/** Which application this build is serving, decided by hostname. */
export type Portal = 'staff' | 'family'

export function currentPortal(): Portal {
  if (typeof window === 'undefined') return 'staff'
  const host = window.location.hostname.toLowerCase()
  if (host.startsWith('myeducation.')) return 'family'
  if (host.startsWith('education.')) return 'staff'
  // Local development and preview environments cannot express the split by
  // hostname, so they fall back to a stored choice the login page sets.
  const stored = window.localStorage.getItem('cornerstone.portal')
  return stored === 'family' ? 'family' : 'staff'
}

/**
 * The address of the other portal, or null when the hostname cannot express
 * the split.
 *
 * Derived by swapping the subdomain rather than hardcoded, so the same code
 * serves production, a staging domain, and anything else that follows the
 * `education.` / `myeducation.` convention. Returns null on localhost and on
 * the generic `*.azurestaticapps.net` hostname, which serve both portals and
 * select with a header — there is nowhere to send someone, so the caller
 * offers the stored-preference switch instead of a dead link.
 */
export function otherPortalUrl(): string | null {
  if (typeof window === 'undefined') return null
  const { protocol, hostname, port } = window.location
  const swapped = hostname.toLowerCase().startsWith('myeducation.')
    ? hostname.replace(/^myeducation\./i, 'education.')
    : hostname.toLowerCase().startsWith('education.')
      ? hostname.replace(/^education\./i, 'myeducation.')
      : null
  if (!swapped) return null
  return `${protocol}//${swapped}${port ? `:${port}` : ''}`
}

export function setLocalPortal(portal: Portal): void {
  try {
    window.localStorage.setItem('cornerstone.portal', portal)
  } catch {
    // Private browsing. The hostname check still works in production.
  }
}

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, unknown>
  correlationId?: string
}

export type {
  JoinPreview,
  TesterSummary,
  TesterRow,
  TesterDetail,
  CohortRow,
  EmailQueueState,
  FeedbackLabState,
  MyFeedbackItem,
  IdeaRow,
  MissionRow,
  EarnedBadge,
  PollRow,
  StaffFeedbackItem,
  FeedbackInboxState,
} from './feedbackTypes'
import type {
  JoinPreview,
  TesterSummary,
  TesterRow,
  TesterDetail,
  CohortRow,
  EmailQueueState,
  FeedbackLabState,
  MyFeedbackItem,
  IdeaRow,
  MissionRow,
  EarnedBadge,
  PollRow,
  StaffFeedbackItem,
  FeedbackInboxState,
} from './feedbackTypes'

export interface ViewAsOptions {
  canSwitch: boolean
  viewingAs: { userId: string; role: string; label: string } | null
  realName: string
  tenantId: string
  tenantName: string
  roles: { role: string; label: string; accounts: { id: string; name: string; email: string }[] }[]
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>
  readonly correlationId?: string

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.correlationId = body.correlationId
  }

  /** Field-level messages from a validation failure, for form display. */
  get fieldErrors(): Record<string, string> {
    const fields = this.details?.fields
    return typeof fields === 'object' && fields !== null ? (fields as Record<string, string>) : {}
  }
}

type Listener = () => void
const sessionExpiredListeners = new Set<Listener>()

/**
 * Notifies the app that the session is gone.
 *
 * Subscribed to by the auth provider, which clears state and routes to the
 * login page. Without this a 401 mid-session leaves a screen full of stale
 * data and buttons that silently fail.
 */
export function onSessionExpired(listener: Listener): () => void {
  sessionExpiredListeners.add(listener)
  return () => sessionExpiredListeners.delete(listener)
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  query?: Record<string, string | number | boolean | undefined | null>
  /** Suppresses the global session-expired broadcast. Used by the login call. */
  anonymous?: boolean
}

/**
 * Exported so feature modules can define their own typed call surfaces
 * without this file growing a method for every screen in the product.
 */
/*
 * How many requests this tab will have in flight at once.
 *
 * The staff home page mounts around twenty cards, each fetching its own
 * summary from its own effect. Over HTTP/1.1 the browser would have queued
 * those behind six connections; over HTTP/2 it does not, so all twenty
 * leave at the same instant.
 *
 * The API runs on Flex Consumption with a per-instance concurrency of 16,
 * so a single person opening the home page exceeds one instance and makes
 * the platform start another. A cold instance on this app takes around 45
 * seconds to answer — `/api/health`, which touches no data, was measured at
 * 45.3s cold against 0.24s warm. One user opening one page should not be
 * able to cause that, and below the limit it cannot.
 *
 * Eight rather than sixteen: the ceiling is per instance, and leaving room
 * means a second person arriving at the same moment is absorbed too.
 */
const MAX_IN_FLIGHT = 8

let inFlight = 0
const waiting: (() => void)[] = []

async function acquireSlot(): Promise<void> {
  if (inFlight < MAX_IN_FLIGHT) {
    inFlight += 1
    return
  }
  await new Promise<void>((resolve) => waiting.push(resolve))
  inFlight += 1
}

function releaseSlot(): void {
  inFlight -= 1
  // Waking exactly one keeps the queue in order and the count honest; a
  // `notifyAll` here would let the whole queue through at once and undo
  // the limit it exists to enforce.
  waiting.shift()?.()
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`/api${path}`, window.location.origin)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value))
  }

  /*
   * Both halves matter here, so both are kept.
   *
   * The gate bounds how many requests this tab has in flight; the retry
   * below waits out an API restart for reads. The slot is held across
   * the retries deliberately: during a restart the server cannot serve
   * anyway, and releasing it would let the queue behind pile more
   * requests onto an instance that is still coming up.
   */
  await acquireSlot()

  const send = () =>
    fetch(url, {
      method: options.method ?? 'GET',
      credentials: 'same-origin',
      signal: options.signal,
      headers: {
        Accept: 'application/json',
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        // Lets local development declare a portal the hostname cannot.
        'X-Cornerstone-Portal': currentPortal(),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    })

  /*
   * While the API restarts after a deploy, the hosting layer answers with a
   * bare 5xx (no JSON body — our own handlers always send one) or drops the
   * connection. A read is safe to repeat, so a GET waits it out briefly
   * instead of showing "Request failed (500)". Writes are never retried: the
   * caller decides whether to send them again.
   */
  const isRead = (options.method ?? 'GET') === 'GET'
  let response: Response
  try {
    // A request abandoned while queued must not be sent at all: the card
    // that wanted it has already unmounted.
    if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError')

    let attempted: Response | null = null
    for (let attempt = 0, delay = 1500; ; attempt += 1, delay = Math.min(delay * 2, 6000)) {
      try {
        attempted = await send()
      } catch (caught) {
        if (!isRead || attempt >= 3 || options.signal?.aborted || !(caught instanceof TypeError)) throw caught
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }
      const bare =
        [500, 502, 503, 504].includes(attempted.status) &&
        !(attempted.headers.get('content-type') ?? '').includes('application/json')
      if (!isRead || !bare || attempt >= 3 || options.signal?.aborted) break
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
    response = attempted
  } finally {
    // Released before the body is read, not after. Reading JSON is work for
    // this tab, not for the server, and holding the slot across it would
    // throttle the app against itself.
    releaseSlot()
  }

  if (response.status === 204) return undefined as T

  let payload: unknown = null
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    payload = await response.json().catch(() => null)
  }

  if (!response.ok) {
    const body = (payload as { error?: ApiErrorBody } | null)?.error ?? {
      code: 'unknown',
      message:
        [500, 502, 503, 504].includes(response.status)
          ? 'The server is not responding. Try again in a moment.'
          : `Request failed (${response.status}).`,
    }

    if (response.status === 401 && !options.anonymous) {
      for (const listener of sessionExpiredListeners) listener()
    }

    throw new ApiError(response.status, body)
  }

  return payload as T
}

// ─────────────────────────────────────────────────────────────────────────────
// Shapes the server returns
// ─────────────────────────────────────────────────────────────────────────────

export interface CurrentUser {
  id: string
  email: string
  displayName: string
  firstName: string
  lastName: string
  role: Role
  roleLabel: string
  schoolIds: string[]
  districtId: string | null
  studentId: string | null
  guardianId: string | null
  staffId: string | null
}

export interface CurrentTenant {
  id: string
  name: string
  slug: string
  isDistrict: boolean
  timezone: string
  locale: string
}

export interface EntitlementState {
  edition: Edition
  active: boolean
  features: Feature[]
}

export interface SessionState {
  user: CurrentUser
  tenant: CurrentTenant
  entitlements: EntitlementState
  permissions: Partial<Record<Module, Action[]>>
  scope?: {
    schoolIds: string[]
    assignedSectionIds: string[]
    guardianStudentIds: string[]
    caseloadStudentIds: string[]
    studentId: string | null
  }
  impersonatedBy?: string | null
  mustChangePassword?: boolean
  /** The account must set up two-step verification before anything else. */
  mfaEnrollmentRequired?: boolean
  /**
   * Set only for a platform operator working inside a customer tenant: the
   * tenant their own account belongs to. `tenant` is then the one on screen,
   * which is what every other field describes.
   */
  homeTenantId?: string | null
  expiresAt: string
  /** The person's own display preferences; absent on the login response. */
  preferences?: import('./preferences').UserPreferences
}

export interface PlatformTenant {
  id: string
  name: string
  slug: string
  edition: Edition
  subscriptionStatus: string
  subscriptionExpiresAt: string | null
  isHome: boolean
  isActive: boolean
}

export interface InstitutionChoice {
  tenantId: string
  institutionName: string
}

export type LoginResult =
  | { kind: 'signed_in'; session: SessionState }
  | { kind: 'choose_institution'; options: InstitutionChoice[] }
  | { kind: 'wrong_portal'; portal: Portal; message: string }
  | { kind: 'mfa_required'; message: string; invalid: boolean }

export interface StudentSummary {
  id: string
  studentNumber: string
  firstName: string
  lastName: string
  preferredName: string | null
  displayName: string
  gradeLevel: string
  schoolId: string
  enrollmentStatus: string
  /**
   * The stored path, which a browser cannot read — blob access is private.
   * It stays on the shape because other callers still pass it around; the two
   * fields below are the ones an avatar is drawn from.
   */
  photoBlobPath: string | null
  /** A short-lived signed link, minted per request. Null means no photo. */
  photoUrl: string | null
  /** How that photo is framed as a circle. Null means dead-centre, unzoomed. */
  photoTransform: PhotoTransform | null
}

export const api = {
  /**
   * Signs in.
   *
   * Three outcomes rather than two, because one address can belong to several
   * institutions and because an account may live on the other portal. Both are
   * real states the UI has to render, not errors.
   */
  async login(input: {
    email: string
    password: string
    rememberMe?: boolean
    tenantId?: string
    mfaCode?: string
  }): Promise<LoginResult> {
    const url = new URL('/api/auth/login', window.location.origin)
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Cornerstone-Portal': currentPortal() },
      body: JSON.stringify(input),
    })

    const payload = await response.json().catch(() => null)

    if (response.status === 300 && payload?.chooseInstitution) {
      return { kind: 'choose_institution', options: payload.chooseInstitution as InstitutionChoice[] }
    }

    if (response.status === 409 && payload?.error?.code === 'wrong_portal') {
      return {
        kind: 'wrong_portal',
        portal: (payload.error.details?.portal as Portal) ?? 'staff',
        message: payload.error.message as string,
      }
    }

    // Two-step verification: the password was right; the code is the next step.
    if (response.status === 401 && payload?.error?.code === 'mfa_required') {
      return { kind: 'mfa_required', message: payload.error.message as string, invalid: Boolean(payload.error.details?.invalid) }
    }

    if (!response.ok) {
      throw new ApiError(response.status, payload?.error ?? { code: 'unknown', message: 'Sign-in failed.' })
    }

    return { kind: 'signed_in', session: payload as SessionState }
  },

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  me: (signal?: AbortSignal) => request<SessionState>('/auth/me', { signal, anonymous: true }),

  /** Platform operations. Every one of these 403s for an ordinary account. */
  platformTenants: (signal?: AbortSignal) =>
    request<{ items: PlatformTenant[]; activeTenantId: string; homeTenantId: string }>('/platform/tenants', {
      signal,
    }),

  switchTenant: (tenantId: string | null) =>
    request<{ activeTenantId: string; homeTenantId: string; tenantName: string | null }>(
      '/platform/active-tenant',
      { method: 'POST', body: { tenantId } },
    ),

  viewAsOptions: (signal?: AbortSignal) => request<ViewAsOptions>('/view-as', { signal }),

  setViewAs: (userId: string | null) =>
    request<{ viewingAs: { userId: string; name: string; role: string; label: string } | null }>('/view-as', {
      method: 'POST',
      body: { userId },
    }),

  demoStatus: (signal?: AbortSignal) =>
    request<{
      tenantId: string
      name: string
      datasetVersion: string | null
      lastResetAt: string | null
      counts: { students: number; sections: number; assignments: number }
      resetCommand: string
      checkCommand: string
    }>('/platform/demo', { signal }),

  // ── Focus group programme ───────────────────────────────────────────────
  joinPreview: (token: string, signal?: AbortSignal) =>
    request<JoinPreview>(`/join/${encodeURIComponent(token)}`, { signal, anonymous: true }),

  join: (input: { token: string; password: string; agreed: boolean }) =>
    request<{ ok: true; expiresAt: string }>('/join', { method: 'POST', body: input, anonymous: true }),

  focusGroupTesters: (query: Record<string, string | undefined> = {}, signal?: AbortSignal) =>
    request<{ summary: TesterSummary; items: TesterRow[] }>('/focus-group/testers', { query, signal }),

  focusGroupTester: (id: string, signal?: AbortSignal) =>
    request<TesterDetail>(`/focus-group/testers/${id}`, { signal }),

  createFocusGroupTester: (input: Record<string, unknown>) =>
    request<{ tester: { id: string }; invitation: { id: string; expiresAt: string } | null }>(
      '/focus-group/testers',
      { method: 'POST', body: input },
    ),

  updateFocusGroupTester: (id: string, input: Record<string, unknown>) =>
    request<{ tester: Record<string, unknown> }>(`/focus-group/testers/${id}`, { method: 'PATCH', body: input }),

  resendInvitation: (id: string) =>
    request<{ invitation: { id: string; expiresAt: string } }>(`/focus-group/testers/${id}/invite`, {
      method: 'POST',
      body: {},
    }),

  focusGroupCohorts: (signal?: AbortSignal) =>
    request<{ items: CohortRow[] }>('/focus-group/cohorts', { signal }),

  createCohort: (input: Record<string, unknown>) =>
    request<{ cohort: CohortRow }>('/focus-group/cohorts', { method: 'POST', body: input }),

  emailQueue: (signal?: AbortSignal) =>
    request<EmailQueueState>('/focus-group/emails', { signal }),

  drainEmailQueue: () =>
    request<{ attempted: number; sent: number; failed: number }>('/focus-group/emails/send', { method: 'POST', body: {} }),

  testSendEmail: (input: { to: string; templateKey: string }) =>
    request<{ sent: boolean; status: string; failureReason: string | null }>('/focus-group/emails/test', {
      method: 'POST',
      body: input,
    }),

  // ── Feedback Lab ────────────────────────────────────────────────────────
  feedbackMe: (signal?: AbortSignal) => request<FeedbackLabState>('/feedback/me', { signal }),

  submitFeedback: (input: Record<string, unknown>) =>
    request<{ feedback: { id: string } }>('/feedback', { method: 'POST', body: input }),

  myFeedback: (signal?: AbortSignal) => request<{ items: MyFeedbackItem[] }>('/feedback/mine', { signal }),

  reportBug: (input: Record<string, unknown>) =>
    request<{ bug: { id: string } }>('/feedback/bugs', { method: 'POST', body: input }),

  listIdeas: (query: Record<string, string | undefined> = {}, signal?: AbortSignal) =>
    request<{ items: IdeaRow[] }>('/feedback/ideas', { query, signal }),

  createIdea: (input: Record<string, unknown>) =>
    request<{ idea: { id: string } }>('/feedback/ideas', { method: 'POST', body: input }),

  voteOnIdea: (id: string) =>
    request<{ voteCount: number; hasVoted: boolean }>(`/feedback/ideas/${id}/vote`, { method: 'POST', body: {} }),

  registerScreenshot: (input: { fileName: string; contentType: string; byteSize: number; caption?: string }) =>
    request<{
      screenshot: { id: string }
      upload: { url: string; method: 'PUT'; headers: Record<string, string>; expiresInSeconds: number }
    }>('/feedback/screenshots', { method: 'POST', body: input }),

  screenshotDownload: (id: string, signal?: AbortSignal) =>
    request<{ url: string; contentType: string; caption: string | null }>(
      `/feedback/screenshots/${id}/download`,
      { signal },
    ),

  listMissions: (signal?: AbortSignal) => request<{ items: MissionRow[] }>('/feedback/missions', { signal }),

  startMission: (id: string) =>
    request<{ attempt: { id: string } }>(`/feedback/missions/${id}/start`, { method: 'POST', body: {} }),

  completeMission: (id: string, input: Record<string, unknown>) =>
    request<{ attempt: { id: string; completed: boolean }; newBadges: string[] }>(
      `/feedback/missions/${id}/complete`,
      { method: 'POST', body: input },
    ),

  myBadges: (signal?: AbortSignal) =>
    request<{ earned: EarnedBadge[]; available: EarnedBadge[] }>('/feedback/badges', { signal }),

  listPolls: (signal?: AbortSignal) => request<{ items: PollRow[] }>('/feedback/polls', { signal }),

  respondToPoll: (id: string, optionKey: string) =>
    request<{ answered: boolean }>(`/feedback/polls/${id}/respond`, { method: 'POST', body: { optionKey } }),

  feedbackInbox: (query: Record<string, string | undefined> = {}, signal?: AbortSignal) =>
    request<FeedbackInboxState>('/feedback/inbox', { query, signal }),

  triageFeedback: (id: string, testerId: string, input: Record<string, unknown>) =>
    request<{ item: StaffFeedbackItem }>(`/feedback/inbox/${id}`, { method: 'PATCH', body: input, query: { testerId } }),

  addFeedbackNote: (id: string, testerId: string, input: { note: string; visibility: 'internal' | 'tester'; notify?: boolean }) =>
    request<{ item: StaffFeedbackItem }>(`/feedback/inbox/${id}/notes`, {
      method: 'POST',
      body: input,
      query: { testerId },
    }),

  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    request<{ ok: true }>('/auth/change-password', { method: 'POST', body: input }),

  health: () => request<{ status: string; checks: Record<string, string> }>('/health', { anonymous: true }),

  listStudents: (
    query: {
      search?: string
      gradeLevel?: string
      schoolId?: string
      status?: string
      sectionId?: string
      limit?: number
      cursor?: string
    } = {},
    signal?: AbortSignal,
  ) => request<{
      items: StudentSummary[]
      cursor: string | null
      /** Every match, not just this page. First page only. */
      total?: number
      scopeEmpty?: boolean
    }>('/students', { query, signal }),

  getStudent: (id: string, signal?: AbortSignal) =>
    request<{ student: StudentSummary & Record<string, unknown> }>(`/students/${encodeURIComponent(id)}`, { signal }),

  /**
   * The children linked to the signed-in guardian, or the signed-in student.
   *
   * A separate endpoint from `listStudents` because a guardian does not hold
   * the `students` permission at all — they hold `childProfile`, which is the
   * right to see their own children rather than to browse a roster.
   */
  familyStudents: (signal?: AbortSignal) =>
    request<{ items: StudentSummary[]; linked: boolean }>('/family/students', { signal }),

  familyStudent: (id: string, signal?: AbortSignal) =>
    request<{ student: StudentSummary & Record<string, unknown> }>(
      `/family/students/${encodeURIComponent(id)}`,
      { signal },
    ),

  familyToday: (studentId: string, signal?: AbortSignal) =>
    request<FamilyToday>(`/family/students/${encodeURIComponent(studentId)}/today`, { signal }),

  familyGrades: (studentId: string, signal?: AbortSignal) =>
    request<FamilyGrades>(`/family/students/${encodeURIComponent(studentId)}/grades`, { signal }),

  familySubmission: (studentId: string, assignmentId: string, signal?: AbortSignal) =>
    request<{ items: import('./familyTypes').SubmissionAttempt[] }>(
      `/family/students/${studentId}/assignments/${assignmentId}/submission`,
      { signal },
    ),

  familySubmit: (studentId: string, assignmentId: string, input: Record<string, unknown>) =>
    request<{ submission: { id: string; attemptNumber: number; late: boolean } }>(
      `/family/students/${studentId}/assignments/${assignmentId}/submission`,
      { method: 'POST', body: input },
    ),

  familyStageSubmissionFile: (
    studentId: string,
    assignmentId: string,
    input: { fileName: string; contentType: string; byteSize: number },
  ) =>
    request<{
      file: { blobPath: string }
      upload: { url: string; method: 'PUT'; headers: Record<string, string> }
    }>(`/family/students/${studentId}/assignments/${assignmentId}/submission-files`, {
      method: 'POST',
      body: input,
    }),

  familySubmissionFile: (studentId: string, assignmentId: string, path: string) =>
    request<{ url: string }>(`/family/students/${studentId}/assignments/${assignmentId}/submission/file`, {
      query: { path },
    }),

  listSubmissions: (sectionId: string, assignmentId: string, signal?: AbortSignal) =>
    request<{ items: import('./familyTypes').TeacherSubmissionRow[] }>(
      `/sections/${sectionId}/assignments/${assignmentId}/submissions`,
      { signal },
    ),

  submissionFileUrl: (sectionId: string, path: string) =>
    request<{ url: string }>(`/sections/${sectionId}/submission-files`, { query: { path } }),

  returnSubmission: (sectionId: string, submissionId: string, comment: string) =>
    request<{ submission: { id: string } }>(`/sections/${sectionId}/submissions/${submissionId}/return`, {
      method: 'POST',
      body: { comment },
    }),

  familyAssignments: (studentId: string, filter: 'upcoming' | 'missing' | 'all', signal?: AbortSignal) =>
    request<FamilyAssignments>(`/family/students/${encodeURIComponent(studentId)}/assignments`, {
      query: { filter },
      signal,
    }),

  familyAttendance: (studentId: string, signal?: AbortSignal) =>
    request<FamilyAttendance>(`/family/students/${encodeURIComponent(studentId)}/attendance`, { signal }),

  // -- Academics ------------------------------------------------------------

  academicCalendar: (schoolId?: string, signal?: AbortSignal) =>
    request<AcademicCalendar>('/academics/calendar', { query: { schoolId }, signal }),

  listCourses: (query: { search?: string; department?: string; limit?: number } = {}, signal?: AbortSignal) =>
    request<{ items: CourseSummary[]; cursor: string | null }>('/courses', { query, signal }),

  listSections: (
    query: { termId?: string; courseId?: string; teacherStaffId?: string; mine?: boolean; limit?: number } = {},
    signal?: AbortSignal,
  ) =>
    request<{ items: SectionSummary[]; cursor: string | null; scopeEmpty?: boolean }>('/sections', {
      query,
      signal,
    }),

  sectionRoster: (sectionId: string, signal?: AbortSignal) =>
    request<{ section: Record<string, unknown>; students: RosterStudent[] }>(
      `/sections/${encodeURIComponent(sectionId)}/roster`,
      { signal },
    ),

  listStaff: (query: { teachersOnly?: boolean; search?: string; limit?: number } = {}, signal?: AbortSignal) =>
    request<{ items: StaffSummary[]; cursor: string | null }>('/staff', { query, signal }),

  mySchedule: (signal?: AbortSignal) =>
    request<{ hasSections: boolean; entries: TeacherScheduleEntry[] }>('/me/schedule', { signal }),

  studentSchedule: (studentId: string, signal?: AbortSignal) =>
    request<{ student: Record<string, unknown>; entries: ScheduleEntry[]; totalCredits: number }>(
      `/students/${encodeURIComponent(studentId)}/schedule`,
      { signal },
    ),

  studentProfile: (studentId: string, signal?: AbortSignal) =>
    request<StudentProfile>(`/students/${encodeURIComponent(studentId)}/profile`, { signal }),

  createStudent: (input: Record<string, unknown>) =>
    request<{ student: StudentSummary }>('/students', { method: 'POST', body: input }),

  updateStudent: (id: string, input: Record<string, unknown>) =>
    request<{ student: StudentSummary }>(`/students/${encodeURIComponent(id)}`, { method: 'PATCH', body: input }),

  /** Mints the write-only storage link a photo uploads through. */
  studentPhotoUploadUrl: (id: string, contentType: string) =>
    request<{ upload: { url: string; method: 'PUT'; headers: Record<string, string> }; blobPath: string }>(
      `/students/${encodeURIComponent(id)}/photo`,
      { method: 'POST', body: { contentType } },
    ),

  /**
   * Points the record at an uploaded photo once storage has it. A new picture
   * clears the old framing server-side, which is why the transform comes back.
   */
  studentPhotoConfirm: (id: string, blobPath: string) =>
    request<{ photoUrl: string; photoTransform: PhotoTransform | null }>(
      `/students/${encodeURIComponent(id)}/photo/confirm`,
      { method: 'POST', body: { blobPath } },
    ),

  /**
   * Saves how the photo is framed as an avatar; null clears it back to
   * dead-centre. The server clamps whatever it is sent, so what comes back is
   * what every avatar will draw.
   */
  studentPhotoTransform: (id: string, transform: PhotoTransform | null) =>
    request<{ photoTransform: PhotoTransform | null }>(`/students/${encodeURIComponent(id)}/photo/transform`, {
      method: 'PUT',
      body: transform,
    }),

  /** Takes the photo off the record, framing and all. */
  studentPhotoRemove: (id: string) =>
    request<{ removed: boolean }>(`/students/${encodeURIComponent(id)}/photo`, { method: 'DELETE' }),

  /** Mints the write-only storage link a staff photo uploads through. */
  staffPhotoUploadUrl: (id: string, contentType: string) =>
    request<{ upload: { url: string; method: 'PUT'; headers: Record<string, string> }; blobPath: string }>(
      `/staff/${encodeURIComponent(id)}/photo`,
      { method: 'POST', body: { contentType } },
    ),

  /** Points the staff record at an uploaded photo once storage has it. */
  staffPhotoConfirm: (id: string, blobPath: string) =>
    request<{ photoUrl: string; photoTransform: PhotoTransform | null }>(
      `/staff/${encodeURIComponent(id)}/photo/confirm`,
      { method: 'POST', body: { blobPath } },
    ),

  /** The staff side of the framing save; null clears it. */
  staffPhotoTransform: (id: string, transform: PhotoTransform | null) =>
    request<{ photoTransform: PhotoTransform | null }>(`/staff/${encodeURIComponent(id)}/photo/transform`, {
      method: 'PUT',
      body: transform,
    }),

  /** Takes the staff photo off the record, framing and all. */
  staffPhotoRemove: (id: string) =>
    request<{ removed: boolean }>(`/staff/${encodeURIComponent(id)}/photo`, { method: 'DELETE' }),

  // -- Attendance -----------------------------------------------------------

  sectionAttendance: (sectionId: string, date: string, signal?: AbortSignal) =>
    request<AttendanceRegister>(`/attendance/section/${encodeURIComponent(sectionId)}`, {
      query: { date },
      signal,
    }),

  submitAttendance: (input: {
    sectionId: string
    date: string
    periodId?: string
    entries: { studentId: string; code: string; minutesLate?: number; note?: string }[]
  }) =>
    request<{ saved: boolean; exceptions: number; presentByDefault: number }>('/attendance/section', {
      method: 'POST',
      body: input,
    }),

  correctAttendance: (input: {
    studentId: string
    schoolId: string
    date: string
    code: string
    note?: string
    reason: string
  }) => request<{ ok: true; recordId: string }>('/attendance/correct', { method: 'POST', body: input }),

  attendanceDaily: (query: { date?: string; schoolId?: string } = {}, signal?: AbortSignal) =>
    request<AttendanceDaily>('/attendance/daily', { query, signal }),

  attendanceConcerns: (query: { schoolId?: string; threshold?: number } = {}, signal?: AbortSignal) =>
    request<{ items: AttendanceConcern[]; threshold: number; enrolledDays: number }>('/attendance/concerns', {
      query,
      signal,
    }),

  studentAttendance: (studentId: string, query: { from?: string; to?: string } = {}, signal?: AbortSignal) =>
    request<{ totals: AttendanceTotals; records: StudentAttendanceRow[] }>(
      `/students/${encodeURIComponent(studentId)}/attendance`,
      { query, signal },
    ),

  // -- Gradebook ------------------------------------------------------------

  gradebook: (sectionId: string, gradingPeriodId?: string, signal?: AbortSignal) =>
    request<GradebookData>(`/sections/${encodeURIComponent(sectionId)}/gradebook`, {
      query: { gradingPeriodId },
      signal,
    }),

  createAssignment: (input: {
    sectionId: string
    categoryId?: string
    title: string
    assignedDate: string
    dueDate: string
    pointsPossible: number
    instructions?: string
    isPublished?: boolean
    unitId?: string
    countsTowardGrade?: boolean
    isExtraCredit?: boolean
    weight?: number
    gradingPeriodId?: string
    rubricId?: string | null
  }) => request<{ assignment: { id: string; title: string } }>('/assignments', { method: 'POST', body: input }),

  updateAssignment: (
    id: string,
    input: {
      sectionId: string
      categoryId?: string
      title?: string
      assignedDate?: string
      dueDate?: string
      pointsPossible?: number
      instructions?: string
      isPublished?: boolean
      countsTowardGrade?: boolean
      isExtraCredit?: boolean
      weight?: number
      gradingPeriodId?: string
      rubricId?: string | null
    },
  ) => request<{ assignment: { id: string } }>(`/assignments/${encodeURIComponent(id)}`, { method: 'PATCH', body: input }),

  saveScores: (input: {
    sectionId: string
    publish?: boolean
    scores: {
      assignmentId: string
      studentId: string
      pointsEarned: number | null
      status: string
      comment?: string
    }[]
  }) =>
    request<{
      saved: number
      rejected: { assignmentId: string; studentId: string; reason: string }[]
      warnings: { assignmentId: string; studentId: string; warning: string }[]
      allSaved: boolean
    }>('/gradebook/scores', { method: 'POST', body: input }),

  postGrades: (input: {
    sectionId: string
    gradingPeriodId: string
    overrides?: { studentId: string; letter: string; percent?: number; reason: string }[]
    comments?: { studentId: string; comment: string }[]
  }) => request<{ posted: number }>('/gradebook/post', { method: 'POST', body: input }),

  studentGrades: (studentId: string, signal?: AbortSignal) =>
    request<{ grades: StudentGrade[]; current?: CurrentGrade[] }>(`/students/${encodeURIComponent(studentId)}/grades`, { signal }),

  // -- Printed documents ----------------------------------------------------

  progressReport: <T>(studentId: string, termId?: string, signal?: AbortSignal) =>
    request<T>(`/reports/progress/${encodeURIComponent(studentId)}`, { query: { termId }, signal }),

  reportCard: <T>(studentId: string, gradingPeriodId: string, signal?: AbortSignal) =>
    request<T>(`/reports/report-card/${encodeURIComponent(studentId)}`, {
      query: { gradingPeriodId },
      signal,
    }),

  transcript: <T>(studentId: string, official = false, signal?: AbortSignal) =>
    request<T>(`/reports/transcript/${encodeURIComponent(studentId)}`, {
      query: { official: official ? 'true' : undefined },
      signal,
    }),

  rosterReport: <T>(sectionId: string, signal?: AbortSignal) =>
    request<T>(`/reports/roster/${encodeURIComponent(sectionId)}`, { signal }),

  // -- Dashboard, analytics and administration ------------------------------

  dashboardSummary: (schoolId?: string, signal?: AbortSignal) =>
    request<DashboardSummary>('/dashboard/summary', { query: { schoolId }, signal }),

  studentSuccess: (studentId: string, signal?: AbortSignal) =>
    request<StudentSuccess>(`/students/${encodeURIComponent(studentId)}/success`, { signal }),

  successCenter: (query: { gradeLevel?: string; limit?: number } = {}, signal?: AbortSignal) =>
    request<SuccessCenter>('/analytics/success', { query, signal }),

  graduationProgress: (studentId: string, signal?: AbortSignal) =>
    request<GraduationProgress>(`/students/${encodeURIComponent(studentId)}/graduation`, { signal }),

  academicAlerts: (query: { limit?: number } = {}, signal?: AbortSignal) =>
    request<{ thresholds: Record<string, number>; examined: number; alerts: AcademicAlert[] }>(
      '/analytics/alerts',
      { query, signal },
    ),

  gradeDistribution: (gradingPeriodId?: string, signal?: AbortSignal) =>
    request<{ total: number; bands: { letter: string; count: number; percent: number }[] }>(
      '/analytics/grade-distribution',
      { query: { gradingPeriodId }, signal },
    ),

  auditLog: (
    query: { studentId?: string; module?: string; from?: string; to?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) => request<{ from: string; to: string; items: AuditEntry[] }>('/audit', { query, signal }),

  recentActivity: (days = 7, signal?: AbortSignal) =>
    request<{
      days: number
      counts: { action: string; label: string; count: number }[]
      items: { id: string; occurredAt: string; summary: string; studentId: string | null; module: string }[]
    }>('/management/activity', { query: { days }, signal }),

  workQueues: (schoolId?: string, signal?: AbortSignal) =>
    request<{ queues: { id: string; label: string; count: number; severity: string; to: string }[] }>(
      '/management/queues',
      { query: { schoolId }, signal },
    ),

  exportData: (dataset: 'students' | 'staff' | 'enrollment' | 'sections', signal?: AbortSignal) =>
    request<{ dataset: string; columns: string[]; rows: (string | number | null)[][]; rowCount: number }>(
      '/management/export',
      { query: { dataset }, signal },
    ),

  importStudents: (input: { schoolId: string; rows: Record<string, string>[]; commit: boolean }) =>
    request<ImportResult>('/management/import/students', { method: 'POST', body: input }),

  studentTimeline: (studentId: string, signal?: AbortSignal) =>
    request<{ items: TimelineEntry[] }>(`/students/${encodeURIComponent(studentId)}/timeline`, { signal }),

  studentDiscipline: (studentId: string, signal?: AbortSignal) =>
    request<{ canSeeDetail: boolean; items: DisciplineEntry[] }>(
      `/students/${encodeURIComponent(studentId)}/discipline`,
      { signal },
    ),

  studentHealth: (studentId: string, signal?: AbortSignal) =>
    request<{ canSeeDetail: boolean; items: HealthEntry[] }>(
      `/students/${encodeURIComponent(studentId)}/health`,
      { signal },
    ),

  studentInterventions: (studentId: string, signal?: AbortSignal) =>
    request<{ items: InterventionEntry[] }>(`/students/${encodeURIComponent(studentId)}/interventions`, { signal }),

  studentDocuments: (studentId: string, signal?: AbortSignal) =>
    request<{ items: DocumentEntry[] }>(`/students/${encodeURIComponent(studentId)}/documents`, { signal }),

  reportBatchScope: (
    query: { scope: string; studentId?: string; sectionId?: string; gradeLevel?: string; schoolId?: string },
    signal?: AbortSignal,
  ) =>
    request<{ count: number; students: { id: string; displayName: string; gradeLevel: string }[] }>(
      '/reports/batch-scope',
      { query, signal },
    ),
}

// -----------------------------------------------------------------------------
// Response shapes used across several screens
// -----------------------------------------------------------------------------

export interface AcademicCalendar {
  academicYears: { id: string; name: string; startDate: string; endDate: string; isCurrent: boolean }[]
  terms: { id: string; name: string; startDate: string; endDate: string; sequence: number; isClosed: boolean }[]
  gradingPeriods: {
    id: string
    termId: string
    name: string
    startDate: string
    endDate: string
    sequence: number
    isOpenForGradeEntry: boolean
  }[]
  periods: { id: string; name: string; shortName: string; sequence: number; startTime: string; endTime: string }[]
}

export interface CourseSummary {
  id: string
  code: string
  name: string
  department: string | null
  credits: number
  weighting: string
  gradeLevels: string[]
  isActive: boolean
}

export interface SectionSummary {
  id: string
  name: string
  sectionNumber: string
  courseId: string
  courseCode: string | null
  courseName: string | null
  credits: number | null
  teacherStaffId: string
  teacherName: string | null
  roomName: string | null
  periodNames: string[]
  meetingDays: number[]
  currentEnrollment: number
  maxCapacity: number | null
  isFull: boolean
  gradingMode: 'points' | 'weighted'
}

export interface StaffSummary {
  id: string
  displayName: string
  firstName: string
  lastName: string
  email: string
  title: string | null
  department: string | null
  isTeacher: boolean
  isActive: boolean
  photoUrl: string | null
  photoTransform: PhotoTransform | null
}

export interface RosterStudent {
  studentId: string
  studentNumber: string
  displayName: string
  gradeLevel: string
  photoBlobPath: string | null
  photoUrl: string | null
  photoTransform: PhotoTransform | null
  alerts: { kind: string; label: string; severity: string }[]
}

export interface TeacherScheduleEntry {
  sectionId: string
  sectionName: string
  courseCode: string | null
  courseName: string
  roomName: string | null
  periodShortName: string | null
  startTime: string | null
  endTime: string | null
  meetingDays: number[]
  meetsToday: boolean
  currentEnrollment: number
  gradingMode: 'points' | 'weighted'
}

export interface ScheduleEntry {
  sectionId: string
  sectionName: string
  courseCode: string | null
  courseName: string
  credits: number | null
  teacherName: string | null
  roomName: string | null
  periodName: string | null
  periodShortName: string | null
  startTime: string | null
  endTime: string | null
  meetingDays: number[]
}

export interface StudentProfile {
  student: StudentSummary & Record<string, unknown>
  guardians: {
    relationshipId: string
    guardianId: string
    name: string
    email: string | null
    phone: string | null
    relationship: string
    isCustodial: boolean
    livesWithStudent: boolean
    canPickUp: boolean
    emergencyPriority: number | null
    hasPortalAccess: boolean
    hasContactRestriction: boolean
    restrictionNote: string | null
    /** Families: the rights in effect today, as labels. */
    rights?: string[]
    primaryContact?: boolean
    directives?: string[]
    contactId?: string
  }[]
  /** Restrictions on people who are not contacts, with their directive. */
  familyDirectives?: string[]
  emergencyContacts: {
    id: string
    name: string
    relationship: string
    phone: string
    priority: number
    canPickUp: boolean
  }[]
  enrollments: {
    id: string
    gradeLevel: string
    status: string
    effectiveDate: string
    endDate: string | null
    entryReason: string | null
    exitReason: string | null
    entryCode?: string | null
    exitCode?: string | null
    schoolId?: string
  }[]
}

export interface AttendanceCode {
  code: string
  label: string
  familyLabel?: string
  isPresent: boolean
  isExcused: boolean
  isTardy: boolean
}

export interface AttendanceRegister {
  section: { id: string; name: string; meetingDays: number[] }
  date: string
  meetsToday: boolean
  submitted: boolean
  codes: AttendanceCode[]
  students: {
    studentId: string
    studentNumber: string
    displayName: string
    photoBlobPath: string | null
    photoUrl: string | null
    photoTransform: PhotoTransform | null
    code: string | null
    note: string | null
    wasCorrected: boolean
  }[]
}

export interface AttendanceDaily {
  date: string
  schoolId: string
  summary: { absent: number; tardy: number; excused: number; unexcused: number; recordsToday: number }
  missingSubmissions: { sectionId: string; sectionName: string; teacherStaffId: string }[]
  entries: {
    recordId: string
    /** How many of the day's registers name this student. */
    classes: number
    studentId: string
    studentNumber: string | null
    displayName: string
    gradeLevel: string | null
    code: string
    codeLabel: string
    isExcused: boolean
    isTardy: boolean
    note: string | null
    wasCorrected: boolean
  }[]
}

export interface AttendanceConcern {
  studentId: string
  studentNumber: string
  displayName: string
  gradeLevel: string
  level: 'approaching' | 'chronic'
  daysAbsent: number
  absenceRate: number | null
  unexcused: number
  tardies: number
}

export interface AttendanceTotals {
  enrolledDays: number
  daysPresent: number
  daysAbsent: number
  excusedAbsences: number
  unexcusedAbsences: number
  tardies: number
  attendanceRate: number | null
  chronicallyAbsent: boolean
  absenceRate: number | null
}

export interface StudentAttendanceRow {
  id: string
  date: string
  code: string
  label: string
  familyLabel: string
  isExcused: boolean
  isTardy: boolean
  note: string | null
}

export interface GradebookData {
  section: { id: string; name: string; gradingMode: 'points' | 'weighted'; termId: string }
  scale: {
    id: string
    name: string
    passingPercent: number
    bands: { letter: string; minPercent: number; gradePoints: number; earnsCredit: boolean }[]
  }
  categories: { id: string; name: string; weight: number; dropLowest?: number; weightTotal: number }[]
  assignments: {
    id: string
    title: string
    categoryId: string | null
    dueDate: string
    pointsPossible: number
    countsTowardGrade: boolean
    allowsSubmission: boolean
    isPublished: boolean
  }[]
  students: {
    studentId: string
    studentNumber: string
    displayName: string
    photoUrl: string | null
    photoTransform: PhotoTransform | null
    scores: Record<string, { id: string; pointsEarned: number | null; status: string; comment: string | null }>
    percent: number | null
    letter: string
    missingCount: number
    categoryAverages: { categoryId: string; name: string; percent: number | null }[]
  }[]
  stats: { assignmentCount: number; studentCount: number; graded: number; failing: number }
}

/** Where a student stands in one class now, from the gradebook. */
export interface CurrentGrade {
  sectionId: string
  sectionName: string
  courseName: string | null
  courseCode: string | null
  teacherName: string | null
  percent: number | null
  letter: string | null
  missing: number
  graded: number
}

export interface StudentGrade {
  id: string
  sectionId: string
  sectionName: string | null
  courseCode: string | null
  courseName: string | null
  letter: string | null
  percent: number | null
  calculatedLetter: string | null
  calculatedPercent: number | null
  wasOverridden: boolean
  comment: string | null
  gradingPeriodId: string | null
}

// -----------------------------------------------------------------------------
// Family portal
// -----------------------------------------------------------------------------

export interface FamilyToday {
  date: string
  student: { id: string; displayName: string; gradeLevel: string }
  attendanceToday: { label: string; isPresent: boolean }
  classesToday: {
    sectionId: string
    courseName: string
    teacherName: string | null
    periodShortName: string | null
    startTime: string | null
  }[]
}

export interface FamilyGrades {
  student: { id: string; displayName: string; gradeLevel: string }
  classes: {
    sectionId: string
    courseName: string
    courseCode: string | null
    teacherName: string | null
    percent: number | null
    letter: string
    missingCount: number
    averageHidden?: boolean
    gradingPeriod?: string | null
    display?: string
    missingAssignments: { id: string; title: string; dueDate: string; pointsPossible: number }[]
    recentAssignments: {
      id: string
      title: string
      dueDate: string
      pointsEarned: number | null
      pointsPossible: number
      status: string
      statusLabel: string
      comment: string | null
    }[]
    /** Plain-English explanation of where the class stands. */
    summary: string
  }[]
  summary: { classCount: number; missingTotal: number; needsAttention: number }
}

export interface FamilyAssignments {
  filter: string
  items: {
    id: string
    sectionId: string
    courseName: string
    title: string
    instructions: string | null
    dueDate: string
    isOverdue: boolean
    dueSoon: boolean
    pointsEarned: number | null
    pointsPossible: number
    percent: number | null
    status: string
    statusLabel: string
    teacherComment: string | null
    allowsSubmission: boolean
    submission: { attemptNumber: number; submittedAt: string; returnedForRevision: boolean } | null
  }[]
}

export interface FamilyAttendance {
  from: string
  to: string
  totals: AttendanceTotals
  records: {
    id: string
    date: string
    /** Family-facing wording, not the attendance code. */
    label: string
    isExcused: boolean
    isTardy: boolean
    needsExplanation: boolean
    /** How many class registers marked this day; a full-day absence covers every class. */
    classes?: number
    note: string | null
  }[]
  /** A sentence describing the record, rather than a bare number. */
  message: string
}

// -----------------------------------------------------------------------------
// Dashboard, analytics and administration
// -----------------------------------------------------------------------------

export interface DashboardSummary {
  date: string
  term: {
    academicYear: string | null
    gradingPeriod: string | null
    gradingPeriodEnds: string | null
    daysRemaining: number | null
    gradeEntryOpen: boolean
  }
  today: {
    totalStudents: number
    /** False until any attendance is recorded today; the three counts below are then null. */
    attendanceTaken: boolean
    presentToday: number | null
    absentToday: number | null
    tardyToday: number | null
    attendanceRate: number | null
    classesMeeting: number
  }
  /** Every row carries its own destination; a row without one is not returned. */
  needsAttention: {
    id: string
    label: string
    count: number
    severity: 'critical' | 'warning' | 'info'
    to: string
  }[]
  announcements: { id: string; title: string; body: string; createdAt: string }[]
  upcoming: { id: string; title: string; startDate: string; kind: string }[]
  gradeLevels: { gradeLevel: string; count: number }[]
}

export interface SuccessIndicator {
  area: 'academic' | 'attendance' | 'behavior' | 'engagement'
  level: 'good' | 'watch' | 'at_risk' | 'critical'
  /** Always names the numbers behind the verdict. */
  reason: string
}

export interface StudentSuccess {
  student: { id: string; displayName: string; gradeLevel: string }
  overall: 'good' | 'watch' | 'at_risk' | 'critical'
  indicators: SuccessIndicator[]
  evidence: {
    gpa: number | null
    coursesGraded: number
    coursesFailing: number
    daysAbsent: number
    absenceRate: number | null
    unexcusedAbsences: number
    tardies: number
    disciplineIncidents: number
  }
}

export interface SuccessCenter {
  examined: number
  isComplete: boolean
  summary: { good: number; watch: number; atRisk: number; critical: number }
  items: {
    studentId: string
    displayName: string
    gradeLevel: string
    overall: 'good' | 'watch' | 'at_risk' | 'critical'
    reasons: string[]
    evidence: StudentSuccess['evidence']
  }[]
}

export interface GraduationProgress {
  student: { id: string; displayName: string; gradeLevel: string; graduationCohort: string | null }
  categories: {
    category: string
    required: number
    earned: number
    inProgress: number
    remaining: number
    isMet: boolean
    onTrackIfPassed: boolean
  }[]
  totals: { required: number; earned: number; inProgress: number; remaining: number }
  onTrack: boolean
  atRisk: string[]
  hasRequirementsConfigured: boolean
}

export interface AcademicAlert {
  studentId: string
  displayName: string
  gradeLevel: string
  kind: string
  message: string
  severity: 'warning' | 'critical'
}

export interface AuditEntry {
  id: string
  occurredAt: string
  actor: string
  actorRole: string
  action: string
  module: string
  studentId: string | null
  reason: string | null
  /** A sentence, so the log is readable before anyone opens the JSON. */
  summary: string
  previousValue: unknown
  newValue: unknown
}

export interface ImportResult {
  preview: boolean
  totalRows: number
  wouldImport?: number
  imported?: number
  rejected: { row: number; reason: string; field?: string }[]
  failed?: { row: number; reason: string }[]
  sample?: { row: number; name: string; gradeLevel: string }[]
  results?: { row: number; studentId: string; studentNumber: string }[]
}

export interface TimelineEntry {
  id: string
  occurredAt: string
  category: string
  title: string
  description: string | null
  sourceModule: string
}

export interface DisciplineEntry {
  id: string
  occurredAt: string
  incidentType: string
  location: string | null
  description: string | null
  actionTaken: string | null
  status: string
  parentContactedAt: string | null
  reentryRequired?: boolean
  reentryHeld?: boolean
  actions?: { id: string; actionType: string; label: string; startDate: string; endDate: string | null; daysAssigned: number | null }[]
}

export interface HealthEntry {
  id: string
  kind: string
  title: string
  detail: string | null
  severity: string
  staffAlertLabel: string | null
  expiresOn: string | null
  isExpiring: boolean
}

export interface InterventionEntry {
  id: string
  kind: string
  tier: number
  concern: string
  goal: string
  strategy: string
  startDate: string
  reviewDate: string
  status: string
  isOverdue: boolean
}

export interface DocumentEntry {
  id: string
  category: string
  title: string
  fileName: string
  sizeBytes: number
  uploadedAt: string
  expiresOn: string | null
  isExpiring: boolean
}

export type { SubmissionAttempt, TeacherSubmissionRow } from './familyTypes'

/**
 * A failure that means the server did not answer — the browser could not
 * connect, or the gateway said the app behind it is down. That is what a
 * deploy looks like from the outside for a minute or two. A real answer
 * (wrong password, forbidden, invalid input) is never transient.
 */
export function isTransientFailure(caught: unknown): boolean {
  if (caught instanceof ApiError) return [502, 503, 504].includes(caught.status)
  if (caught instanceof DOMException && caught.name === 'AbortError') return false
  return caught instanceof TypeError
}

/** What to tell people when the server is away, depending on whether their own connection is. */
export function unreachableMessage(): string {
  return typeof navigator !== 'undefined' && navigator.onLine === false
    ? 'You appear to be offline. Check your connection and try again.'
    : 'Cornerstone is restarting after an update and is not answering yet. Try again in a minute.'
}

/**
 * Runs a call, retrying quietly while the server is restarting (up to about
 * 45 seconds), so an update in progress reads as a short wait rather than
 * an error. `onWaiting` fires once, the first time a retry is needed.
 */
export async function retryWhileRestarting<T>(call: () => Promise<T>, opts: { onWaiting?: () => void; signal?: AbortSignal; budgetMs?: number } = {}): Promise<T> {
  const deadline = Date.now() + (opts.budgetMs ?? 45_000)
  let delay = 1500
  let told = false
  for (;;) {
    try {
      return await call()
    } catch (caught) {
      if (!isTransientFailure(caught) || opts.signal?.aborted || Date.now() + delay > deadline) throw caught
      if (!told) { told = true; opts.onWaiting?.() }
      await new Promise((resolve) => setTimeout(resolve, delay))
      delay = Math.min(Math.round(delay * 1.6), 8000)
    }
  }
}
